import type { Node as MdastNode, Root as MdastRoot } from 'mdast';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize, { defaultSchema } from 'rehype-sanitize';
import rehypeStringify from 'rehype-stringify';
import remarkGfm from 'remark-gfm';
import remarkParse from 'remark-parse';
import remarkRehype from 'remark-rehype';
import { unified } from 'unified';
import { SKIP, visit } from 'unist-util-visit';
import remarkGroupedCards from '../remarkGroupedCards';
import remarkSingleCard from '../remarkSingleCard';
import { normalizeSlackToMarkdown } from './slackish';

/**
 * Hast element node type (HTML AST from rehype).
 * Lightweight definition to avoid @types/hast dependency.
 */
type HastElement = {
  type: 'element';
  tagName: string;
  properties?: {
    className?: string | string[];
    [key: string]: unknown;
  };
  children?: unknown[];
};

/**
 * Hast parent node type (node with children).
 * Used for tree manipulation in rehype plugins.
 */
type HastParent = {
  type: string;
  children: unknown[];
};

export type MarkdownClipboardPayload = {
  html: string;
  plainText: string;
};

type ErrorWithCause = Error & { cause?: unknown };

function wrapError(message: string, err: unknown): Error {
  if (err instanceof Error) {
    const wrapped = new Error(message);
    (wrapped as ErrorWithCause).cause = err;
    return wrapped;
  }
  return new Error(message);
}

/**
 * Sanitization schema for clipboard HTML output.
 * Allows basic formatting tags plus GFM (GitHub Flavored Markdown) features:
 * - Headings, paragraphs, lists
 * - Bold, italic, links, blockquotes
 * - Code blocks (pre + code) and inline code
 * - Tables (for GFM table support)
 * - Strikethrough (del) for GFM ~~text~~
 */
const minimalSchema = {
  ...defaultSchema,
  tagNames: [
    'h1',
    'h2',
    'h3',
    'h4',
    'h5',
    'h6',
    'p',
    'ul',
    'ol',
    'li',
    'strong',
    'em',
    'a',
    'blockquote',
    'code',
    'pre',
    'table',
    'thead',
    'tbody',
    'tr',
    'th',
    'td',
    'del',
  ],
  attributes: {
    ...defaultSchema.attributes,
    a: ['href'],
    th: ['align'],
    td: ['align'],
  },
};

function normalizeClassList(properties?: HastElement['properties']): string[] {
  const className = properties?.className;
  if (Array.isArray(className)) {
    return className;
  }
  if (typeof className === 'string') {
    return className.split(/\s+/).filter(Boolean);
  }
  return [];
}

/**
 * Rehype plugin that normalizes custom card blocks for clipboard export.
 * Converts card-block divs to blockquotes and unwraps grouped-cards containers.
 */
function rehypeNormalizeCards() {
  return (tree: any) => {
    visit(tree, 'element', (node: HastElement) => {
      if (node.tagName !== 'div') {
        return;
      }
      const classList = normalizeClassList(node.properties);
      if (!classList.includes('card-block')) {
        return;
      }
      node.tagName = 'blockquote';
      node.properties = {};
    });

    visit(tree, 'element', (node: HastElement, index, parent) => {
      if (node.tagName !== 'div') {
        return;
      }
      const classList = normalizeClassList(node.properties);
      if (!classList.includes('grouped-cards')) {
        return;
      }
      if (!parent || index === undefined) {
        return;
      }
      const parentNode = parent as HastParent;
      parentNode.children.splice(index, 1, ...(node.children ?? []));
      return [SKIP, index];
    });
  };
}

function nodeToPlainText(node: MdastNode): string {
  const joinParts = (parts: string[]): string => {
    let combined = '';

    parts.forEach((part) => {
      if (part === '\n') {
        if (combined && !combined.endsWith('\n')) {
          combined += '\n';
        }
        return;
      }

      const trimmed = part.trim();
      if (!trimmed) {
        return;
      }

      if (!combined || combined.endsWith('\n')) {
        combined += trimmed;
        return;
      }

      if (/^[.,!?;:)\]]/.test(trimmed)) {
        combined += trimmed;
        return;
      }

      combined += ` ${trimmed}`;
    });

    return combined;
  };

  switch (node.type) {
    case 'text':
      return 'value' in node && typeof node.value === 'string' ? node.value : '';
    case 'code':
      return 'value' in node && typeof node.value === 'string' ? node.value : '';
    case 'inlineCode':
      return 'value' in node && typeof node.value === 'string' ? node.value : '';
    case 'break':
      return '\n';
    case 'list': {
      const children = 'children' in node && Array.isArray(node.children) ? node.children : [];
      const isOrderedList = 'ordered' in node && node.ordered === true;
      const orderedStart = 'start' in node && typeof node.start === 'number' ? node.start : 1;
      const items = children
        .map((child) => nodeToPlainText(child))
        .filter(Boolean)
        .map((item, index) => {
          if (!isOrderedList) {
            return `- ${item}`;
          }
          return `${orderedStart + index}. ${item}`;
        });
      return items.join('\n');
    }
    case 'listItem': {
      const children = 'children' in node && Array.isArray(node.children) ? node.children : [];
      const inlineParts: string[] = [];
      const nestedBlocks: string[] = [];

      children.forEach((child) => {
        const plainChild = nodeToPlainText(child).trim();
        if (!plainChild) {
          return;
        }

        if (child.type === 'list') {
          const indented = plainChild
            .split('\n')
            .map((line) => (line.trim() ? `  ${line}` : line))
            .join('\n');
          nestedBlocks.push(indented);
          return;
        }

        inlineParts.push(plainChild);
      });

      const firstLine = joinParts(inlineParts);
      if (nestedBlocks.length === 0) {
        return firstLine;
      }
      return [firstLine, ...nestedBlocks].filter(Boolean).join('\n');
    }
    default:
      if ('children' in node && Array.isArray(node.children) && node.children.length > 0) {
        return joinParts(node.children.map((child) => nodeToPlainText(child)));
      }
      return '';
  }
}

/**
 * Converts markdown to plain text by extracting text content and
 * normalizing structure while preserving list ordering.
 *
 * @param markdown - Raw markdown content
 * @returns Plain text representation with basic structure preserved
 */
function markdownToPlainText(markdown: string): string {
  try {
    const processor = unified()
      .use(remarkParse)
      .use(remarkGfm)
      .use(remarkGroupedCards)
      .use(remarkSingleCard);
    const tree = processor.parse(markdown);
    const transformed = processor.runSync(tree) as MdastRoot;
    const blocks = transformed.children.map((node) => nodeToPlainText(node).trim()).filter(Boolean);
    return blocks.join('\n\n');
  } catch (err) {
    throw wrapError(
      `Failed to convert markdown to plain text: ${err instanceof Error ? err.message : 'Unknown error'}`,
      err
    );
  }
}

/**
 * Converts markdown to sanitized HTML suitable for clipboard.
 * Supports GFM features (tables, strikethrough) and custom card blocks.
 *
 * @param markdown - Raw markdown content
 * @returns HTML string with minimal formatting tags
 */
async function markdownToHtml(markdown: string): Promise<string> {
  try {
    const processor = unified()
      .use(remarkParse)
      .use(remarkGfm)
      .use(remarkGroupedCards)
      .use(remarkSingleCard)
      .use(remarkRehype, { allowDangerousHtml: true })
      .use(rehypeRaw)
      .use(rehypeNormalizeCards)
      .use(rehypeSanitize, minimalSchema)
      .use(rehypeStringify);
    const file = await processor.process(markdown);
    return String(file);
  } catch (err) {
    throw wrapError(
      `Failed to convert markdown to HTML: ${err instanceof Error ? err.message : 'Unknown error'}`,
      err
    );
  }
}

/**
 * Builds clipboard payload with both HTML and plain text formats.
 * Normalizes Slackish markdown syntax and converts custom card blocks to blockquotes.
 *
 * @param markdown - Raw markdown content (may include Slackish syntax)
 * @returns Object containing sanitized HTML and plain text versions
 * @throws {Error} If markdown processing fails
 */
export async function buildMarkdownClipboardPayload(
  markdown: string
): Promise<MarkdownClipboardPayload> {
  try {
    const normalized = normalizeSlackToMarkdown(markdown || '');
    const html = await markdownToHtml(normalized);
    const plainText = markdownToPlainText(normalized);
    return { html, plainText };
  } catch (err) {
    throw wrapError(
      `Failed to build clipboard payload: ${err instanceof Error ? err.message : 'Unknown error'}`,
      err
    );
  }
}

/**
 * Copies markdown content to clipboard in both HTML and plain text formats.
 * Provides progressive fallback across clipboard APIs:
 * 1. Modern Clipboard API with ClipboardItem (dual format)
 * 2. Clipboard API writeText (plain text only)
 * 3. Legacy document.execCommand (plain text only)
 *
 * Browser compatibility:
 * - Chrome/Edge 76+, Safari 13.1+, Firefox 87+ support dual format
 * - Older browsers fall back to plain text only
 *
 * @param markdown - Raw markdown content to copy
 * @throws {Error} If all clipboard methods fail or markdown processing fails
 */
export async function copyMarkdownToClipboard(markdown: string): Promise<void> {
  if (!markdown || markdown.trim() === '') {
    return;
  }

  try {
    const { html, plainText } = await buildMarkdownClipboardPayload(markdown);

    const failures: unknown[] = [];

    if (
      typeof navigator !== 'undefined' &&
      navigator.clipboard?.write &&
      typeof ClipboardItem !== 'undefined'
    ) {
      try {
        const item = new ClipboardItem({
          'text/html': new Blob([html], { type: 'text/html' }),
          'text/plain': new Blob([plainText], { type: 'text/plain' }),
        });
        await navigator.clipboard.write([item]);
        return;
      } catch (err) {
        failures.push(err);
      }
    }

    if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
      try {
        await navigator.clipboard.writeText(plainText);
        return;
      } catch (err) {
        failures.push(err);
      }
    }

    if (typeof document !== 'undefined') {
      let textarea: HTMLTextAreaElement | null = null;
      try {
        textarea = document.createElement('textarea');
        textarea.value = plainText;
        textarea.style.position = 'fixed';
        textarea.style.left = '-9999px';
        document.body.appendChild(textarea);
        textarea.focus();
        textarea.select();
        const ok = document.execCommand('copy');
        if (!ok) {
          throw new Error('document.execCommand("copy") returned false');
        }
        return;
      } catch (err) {
        failures.push(err);
      } finally {
        if (textarea?.parentNode) {
          textarea.parentNode.removeChild(textarea);
        }
      }
    }

    if (failures.length > 0) {
      throw failures[failures.length - 1];
    }

    throw new Error('Clipboard API unavailable');
  } catch (err) {
    throw wrapError(
      `Failed to copy to clipboard: ${err instanceof Error ? err.message : 'Unknown error'}`,
      err
    );
  }
}
